import { ENV } from '../../config/runtimeEnv';
import React, { useState, useEffect } from "react";
import { Card, Button, Form, Input, Radio, Checkbox, Space, Divider, Alert, Typography, Avatar, Badge, Steps, Progress } from "antd";
import { CheckCircleOutlined, UserOutlined, MailOutlined, PhoneOutlined, HeartOutlined, GiftOutlined, ArrowRightOutlined, StarOutlined } from "@ant-design/icons";
import { useNotifications } from "../../context/NotificationContext";
import { fetchSalesOrderPublic } from "../../api/inventory/salesOrderApi";
import { PublicSalesOrder, PublicSalesOrderProduct } from "../../types/inventory/OrderTypes";
import { assignOrderKits } from "../../api/healthcare/PatientApi";
import { NeuralLoader } from "../../components/icons/NeuralLoader";
import { SectionCard } from "../../components/shared";

const { Title, Text } = Typography;

// commerce answers one 404 for a used, rotated or unknown link, so a reused link cannot be told apart from an invalid one.
const INVALID_LINK_MESSAGE = "This link is invalid or has expired — ask the clinic for a new one.";
// K-L2/N3: healthcare's fixed 409 for a kit registered to someone else that can no longer move; same text as RegisterKit.tsx.
const KIT_REGISTERED_ELSEWHERE_MESSAGE = "This kit is registered to someone else. Please contact the clinic on info@mygene.com.au or +61 434 048 778.";

const isKitRegisteredElsewhere = (err: any): boolean =>
	err?.response?.status === 409 && err?.response?.data?.details?.reason === "kit_already_registered";

const assignmentFailureMessage = (err: any): string => {
	if (isKitRegisteredElsewhere(err)) {
		return KIT_REGISTERED_ELSEWHERE_MESSAGE;
	}

	return err?.response?.status === 404 ? INVALID_LINK_MESSAGE : err?.response?.data?.message || "Failed to assign kits. Please try again or contact support.";
};

const OrderAssignmentPage: React.FC = () => {
	// Get ID from URL pathname
	const pathParts = window.location.pathname.split("/");
	const id = pathParts[pathParts.length - 1];
	const token = new URLSearchParams(window.location.search).get("t") || undefined;

	const [form] = Form.useForm();

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [order, setOrder] = useState<PublicSalesOrder | null>(null);
	const [showWelcome, setShowWelcome] = useState(true);
	const [welcomePhase, setWelcomePhase] = useState(0);
	const [step, setStep] = useState<"selection" | "assignment" | "complete">("selection");
	const [isForMe, setIsForMe] = useState<boolean | null>(null);
	const [selectedLabels, setSelectedLabels] = useState<Set<number>>(new Set());
	const [assignments, setAssignments] = useState<Record<number, any>>({});
	const [currentStepProgress, setCurrentStepProgress] = useState(0);

	// Extract customer info for personalization
	const customerFirstName = order?.shopify_customer_name?.split(" ")[0] || "Valued Customer";

	const { showError } = useNotifications();

	useEffect(() => {
		const fetchOrder = async () => {
			if (!id) {
				setError("No order ID provided");
				setLoading(false);
				return;
			}

			try {
				const orderData = await fetchSalesOrderPublic(parseInt(id), token);
				setOrder(orderData);

				// If order test assignment is already completed, go directly to complete step
				if (orderData?.order_test_assignment_completed) {
					setStep("complete");
					setShowWelcome(false);
					setCurrentStepProgress(100);
				} else {
					// Multi-phase welcome animation
					setTimeout(() => setWelcomePhase(1), 1000);
					setTimeout(() => setWelcomePhase(2), 2200);
					setTimeout(() => setWelcomePhase(3), 3500);
					setTimeout(() => {
						setShowWelcome(false);
						// Smooth transition into main content
						setCurrentStepProgress(25);
					}, 5000);
				}
			} catch (err: any) {
				setError(err.response?.status === 404 ? INVALID_LINK_MESSAGE : err.response?.data?.message || "Failed to load order");
			} finally {
				setLoading(false);
			}
		};

		fetchOrder();
	}, [id, token]);

	const totalLabels = order?.items?.reduce((sum, item) => {
		return sum + (item.sales_order_labels?.filter(label => label.stock_label?.label_id).length || 0);
	}, 0) || 0;

	const handleSelectionSubmit = () => {
		setCurrentStepProgress(75);
		if (totalLabels === 1) {
			if (isForMe) {
				// Pre-fill customer info for single label
				const assignment = {
					firstName: order?.shopify_customer_name?.split(" ")[0] || "",
					lastName: order?.shopify_customer_name?.split(" ").slice(1).join(" ") || "",
					email: order?.shopify_customer_email || "",
					phone: order?.shopify_shipping_address?.phone || "",
				};

				// Find the first valid label with label_id
				const validLabel = order?.items?.find(item => 
					item.sales_order_labels?.some(label => label.stock_label?.label_id)
				)?.sales_order_labels?.find(label => label.stock_label?.label_id);
				
				if (validLabel) {
					setAssignments({ [validLabel.stock_label_id]: assignment });
				}
			}
		}
		setTimeout(() => setStep("assignment"), 300);
	};

	const handleAssignmentSubmit = async (values: any) => {
		setSubmitting(true);
		setCurrentStepProgress(100);

		try {
			// Collect all assignments
			const finalAssignments: any[] = [];
			console.log("Order items for assignment:", order);
			order?.items?.forEach((item) => {
				item.sales_order_labels?.forEach((label) => {
					const labelId = label.stock_label_id;
					console.log("Processing label ID:", labelId, assignments);
					const assignment = assignments[labelId] || values[`label_${labelId}`];
					console.log("Processing assignment for label:", labelId, assignment);
					
					// Only proceed if assignment exists AND label has a valid label_id
					if (assignment && label.stock_label?.label_id) {
						finalAssignments.push({
							label_id: label.stock_label.label_id,
							stock_label_id: labelId,
							product_name: item.product?.name || "",
							firstName: assignment.firstName,
							lastName: assignment.lastName,
							email: assignment.email,
							phone: assignment.phone || "",
						});
					} else if (assignment && !label.stock_label?.label_id) {
						console.warn("Skipping assignment - invalid label_id for label:", labelId, label);
					}
				});
			});

			await assignOrderKits({
				sales_order_id: order!.sales_order_id,
				assignments: finalAssignments,
			}, token);

			// Show success state
			setStep("complete" as any);
		} catch (error: any) {
			console.error("Assignment error:", error);
			showError("Assignment Failed", assignmentFailureMessage(error));
		} finally {
			setSubmitting(false);
		}
	};

	const getPrimaryImage = (product: PublicSalesOrderProduct) => {
			if (!product.images || product.images.length === 0) return null;
			return product.images.find((img) => img.is_primary) || product.images[0];
		};

	const getImageUrl = (fileId: string) => {
		return `${ENV.VITE_FILE_SERVER}/inventory/display/${fileId}`;
	}

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white">
				<NeuralLoader size="full" text="" fullscreen={true} backgroundColor="black" networkColor="white" />
			</div>
		);
	}

	if (error || !order) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white p-6">
				<Alert message="Error Loading Order" description={error || "Order not found"} type="error" showIcon />
			</div>
		);
	}

	if (showWelcome) {
		return (
			<div className="min-h-screen relative">
				{/* NeuralLoader as background */}
				<NeuralLoader size="full" text="" fullscreen={true} backgroundColor="black" networkColor="white" />

				{/* Welcome content overlay */}
				<div className="absolute inset-0 flex items-center justify-center z-[60]">
					<div className="text-center max-w-2xl mx-auto px-6">
						{welcomePhase >= 1 && (
							<div className="animate-slide-up-delayed">
								<Title level={1} className="mb-4 text-6xl font-bold text-white drop-shadow-lg">
									Hello {customerFirstName}
								</Title>
							</div>
						)}

						{welcomePhase >= 2 && (
							<div className="animate-slide-up-super-delayed">
								<Text className="text-xl text-white/90 mb-4 block leading-relaxed drop-shadow">Thank you for choosing us for your health journey</Text>
							</div>
						)}

						{welcomePhase >= 3 && (
							<div className="animate-fade-in-final mt-8">
								<div className="flex justify-center items-center space-x-2 text-lg text-white/80">
									<StarOutlined className="text-yellow-400" />
									<Text className="text-white/80">Let's get your test kit assigned</Text>
									<StarOutlined className="text-yellow-400" />
								</div>
								<div className="mt-4">
									<Progress percent={100} strokeColor="#ffffff" showInfo={false} className="max-w-xs mx-auto" />
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Enhanced CSS animations */}
				<style>{`
					@keyframes slide-up {
						from { opacity: 0; transform: translateY(30px); }
						to { opacity: 1; transform: translateY(0); }
					}
					@keyframes fade-in {
						from { opacity: 0; }
						to { opacity: 1; }
					}
					
					.animate-slide-up-delayed {
						animation: slide-up 0.8s ease-out 0.5s forwards;
						opacity: 0;
					}
					.animate-slide-up-super-delayed {
						animation: slide-up 0.8s ease-out 1.2s forwards;
						opacity: 0;
					}
					.animate-fade-in-final {
						animation: fade-in 1s ease-out 2s forwards;
						opacity: 0;
					}
				`}</style>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-white to-gray-50 transition-all duration-700">
			{/* Enhanced Header with Progress */}
			<div className="border-b border-gray-200 bg-white/95 backdrop-blur-sm shadow-sm">
				<div className="max-w-5xl mx-auto px-6 py-6">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center space-x-4">
							<Avatar size={48} style={{ backgroundColor: "var(--mg-text)" }} icon={<UserOutlined />}>
								{customerFirstName[0]}
							</Avatar>
							<div>
								<Title level={3} className="mb-0">
									Welcome, {customerFirstName}!
								</Title>
								<Text className="text-gray-600">Order #{order.shopify_order_number || order.sales_order_id}</Text>
							</div>
						</div>
						<div className="text-right">
							<Text className="text-sm text-gray-500 block">Test Kits</Text>
							<Text className="text-lg font-semibold">Ready to Assign</Text>
						</div>
					</div>

					{/* Progress Steps */}
					<Steps
						current={step === "selection" ? 0 : 1}
						className="max-w-md"
						items={[
							{
								title: "Review Order",
								icon: <GiftOutlined />,
							},
							{
								title: "Assign Kits",
								icon: <UserOutlined />,
							},
							{
								title: "Complete",
								icon: <CheckCircleOutlined />,
							},
						]}
					/>
					<Progress percent={currentStepProgress} strokeColor="var(--mg-primary)" showInfo={false} className="mt-4" />
				</div>
			</div>

			<div className="max-w-5xl mx-auto px-6 py-8">
				{step === "selection" && (
					<div className="animate-slide-in-right">
						<SectionCard>
							<div className="text-center mb-8">
								<Title level={3} className="text-gray-800 mb-2">
									Your Health Journey Awaits
								</Title>
								<Text className="text-gray-600 text-lg">Let's review what you've ordered and get everything set up perfectly for you</Text>
							</div>

							<div className="space-y-6">
								{order.items?.map((item, idx) => {
									// Only show items that have at least one label with valid label_id
									const validLabels = item.sales_order_labels?.filter(label => label.stock_label?.label_id) || [];
									if (validLabels.length === 0) {
										return null;
									}
									
									return (
									<div key={idx} className="group">
										<Card size="small" className="transition-all duration-300 hover:shadow-md border border-gray-100 hover:border-blue-200" style={{ borderRadius: "12px" }}>
											<div className="flex gap-6 items-start">
												{item.product?.images?.[0] && (
													<div className="relative">
														<img
															src={getImageUrl(getPrimaryImage(item.product)?.file_id || "")}
															alt={item.product.name}
															className="w-24 h-24 object-cover rounded-lg border border-gray-200 shadow-sm group-hover:shadow-md transition-shadow duration-300"
														/>
														<div className="absolute -top-2 -right-2 bg-slate-800 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">{validLabels.length}</div>
													</div>
												)}
												<div className="flex-1">
													<Title level={4} className="mb-2 text-gray-800">
														{item.product?.name}
													</Title>
													<div className="space-y-2">
														<div className="flex items-center space-x-2">
															<Badge status="processing" />
															<Text className="text-gray-600">
																Available Kits: <span className="font-semibold text-slate-800">{validLabels.length}</span>
															</Text>
														</div>
														<div className="flex items-center space-x-2">
															<Badge status="success" />
															<Text className="text-gray-600">
																Kit ID: <span className="font-mono text-slate-800 bg-gray-50 px-2 py-1 rounded">{validLabels[0]?.stock_label?.label_id}</span>
															</Text>
														</div>
													</div>
												</div>
											</div>
										</Card>
									</div>
								)})}
							</div>

							<Divider className="my-8">
								<HeartOutlined className="text-slate-400" />
							</Divider>

							<div className="bg-gradient-to-r from-gray-50 to-white p-8 rounded-2xl border border-gray-100">
								<Title level={4} className="mb-6 text-center text-gray-800">
									{totalLabels === 1 ? "Is this test for you?" : "Are any of these tests for you?"}
								</Title>

								<Radio.Group onChange={(e) => setIsForMe(e.target.value)} value={isForMe} className="w-full">
									<Space direction="vertical" className="w-full" size="large">
										<div className="w-full">
											<Radio value={true} className="hidden" id="radio-true" />
											<label htmlFor="radio-true" className="w-full cursor-pointer block">
												<Card
													size="small"
													className={`w-full transition-all duration-300 hover:shadow-lg border-2 ${isForMe === true ? "border-slate-400 shadow-md" : "border-gray-200 hover:border-slate-300"}`}
													style={{ borderRadius: "12px" }}
												>
													<div className="flex items-center space-x-3 p-2">
														<CheckCircleOutlined className={`text-xl ${isForMe === true ? "text-slate-800" : "text-slate-700"}`} />
														<div>
															<Text strong className="text-lg">
																Yes, {totalLabels === 1 ? "this is" : "at least one is"} for me
															</Text>
															<Text className="block text-gray-500 text-sm mt-1">We'll pre-fill your information to make it easy</Text>
														</div>
													</div>
												</Card>
											</label>
										</div>
										<div className="w-full">
											<Radio value={false} className="hidden" id="radio-false" />
											<label htmlFor="radio-false" className="w-full cursor-pointer block">
												<Card
													size="small"
													className={`w-full transition-all duration-300 hover:shadow-lg border-2 ${isForMe === false ? "border-slate-400 shadow-md" : "border-gray-200 hover:border-slate-300"}`}
													style={{ borderRadius: "12px" }}
												>
													<div className="flex items-center space-x-3 p-2">
														<UserOutlined className={`text-xl ${isForMe === false ? "text-slate-800" : "text-slate-700"}`} />
														<div>
															<Text strong className="text-lg">
																No, {totalLabels === 1 ? "this is" : "these are"} for someone else
															</Text>
															<Text className="block text-gray-500 text-sm mt-1">You can assign to family members or friends</Text>
														</div>
													</div>
												</Card>
											</label>
										</div>
									</Space>
								</Radio.Group>

								<Button
									type="primary"
									size="large"
									block
									className="mt-8 h-14 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl"
									disabled={isForMe === null}
									onClick={handleSelectionSubmit}
									style={{
										borderRadius: "12px",
										background: "linear-gradient(135deg, #000000 0%, #1e293b 100%)",
										border: "none",
									}}
								>
									Continue Your Journey <ArrowRightOutlined className="ml-2" />
								</Button>
							</div>
						</SectionCard>
					</div>
				)}

				{step === "assignment" && (
					<div className="animate-slide-in-left">
						<SectionCard>
							<div className="text-center mb-8">
								<Title level={3} className="text-gray-800 mb-2">
									Almost There, {customerFirstName}!
								</Title>
								<Text className="text-gray-600 text-lg">Let's assign each test kit to the right person</Text>
							</div>

							<Form form={form} layout="vertical" onFinish={handleAssignmentSubmit} initialValues={assignments}>
								{order.items?.map((item, itemIdx) =>
									item.sales_order_labels?.map((label, labelIdx) => {
										const labelId = label.stock_label_id;
										const isMyTest = isForMe && totalLabels === 1;
										const prefilled = assignments[labelId];
										
										// Only render labels that have valid label_ids
										if (!label.stock_label?.label_id) {
											console.warn("Skipping render for label without valid label_id:", labelId, label);
											return null;
										}

										return (
											<div key={`${itemIdx}-${labelIdx}`} className="mb-10">
												<Card size="small" className="border border-gray-200 shadow-sm" style={{ borderRadius: "12px" }}>
													<div className="flex items-center gap-4 mb-6">
														{item.product?.images?.[0] && <img src={getImageUrl(getPrimaryImage(item.product)?.file_id || "")} alt={item.product.name} className="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm" />}
														<div className="flex-1">
															<Title level={5} className="mb-1">
																{item.product?.name}
															</Title>
															<Text className="text-gray-500">
																Kit ID: <span className="font-mono text-slate-800 bg-gray-50 px-2 py-1 rounded">{label.stock_label?.label_id}</span>
															</Text>
														</div>
													</div>

													{isMyTest && prefilled ? (
														<Alert
															message={
																<div className="flex items-center">
																	<CheckCircleOutlined className="mr-2" />
																	This test is assigned to you, {customerFirstName}!
																</div>
															}
															description={`${prefilled.firstName} ${prefilled.lastName}`}
															type="success"
															showIcon={false}
															className="border-gray-200 bg-gray-50 rounded-lg"
														/>
													) : (
														<div className="bg-gray-50 p-6 rounded-xl">
															{isForMe && totalLabels > 1 && (
																<div className="mb-6">
																	<Checkbox
																		checked={selectedLabels.has(labelId)}
																		onChange={(e) => {
																			const newSelected = new Set(selectedLabels);
																			if (e.target.checked) {
																				// Add this label to selected labels
																				newSelected.add(labelId);
																				setSelectedLabels(newSelected);
																				// Pre-fill customer information
																				setAssignments({
																					...assignments,
																					[labelId]: {
																						firstName: order?.shopify_customer_name?.split(" ")[0] || "",
																						lastName: order?.shopify_customer_name?.split(" ").slice(1).join(" ") || "",
																						email: order?.shopify_customer_email || "",
																						phone: order?.shopify_shipping_address?.phone || "",
																					},
																				});
																			} else {
																				// Remove this label from selected labels
																				newSelected.delete(labelId);
																				setSelectedLabels(newSelected);
																				// Remove pre-filled data
																				const newAssignments = { ...assignments };
																				delete newAssignments[labelId];
																				setAssignments(newAssignments);
																			}
																		}}
																		className="text-slate-700"
																	>
																		<span className="font-medium">This test is for me</span>
																	</Checkbox>
																</div>
															)}

															{(!selectedLabels.has(labelId) || !isForMe) && (
																<div className="space-y-5">
																	<div className="grid grid-cols-2 gap-4">
																		<Form.Item name={[`label_${labelId}`, "firstName"]} rules={[{ required: true, message: "First name required" }]}>
																			<Input prefix={<UserOutlined className="text-slate-600" />} placeholder="First Name" size="large" className="rounded-lg" />
																		</Form.Item>
																		<Form.Item name={[`label_${labelId}`, "lastName"]} rules={[{ required: true, message: "Last name required" }]}>
																			<Input prefix={<UserOutlined className="text-slate-600" />} placeholder="Last Name" size="large" className="rounded-lg" />
																		</Form.Item>
																	</div>
																	<Form.Item
																		name={[`label_${labelId}`, "email"]}
																		rules={[
																			{ type: "email", message: "Please enter a valid email" },
																			({ getFieldValue }) => ({
																				validator(_, value) {
																					const phone = getFieldValue([`label_${labelId}`, "phone"]);
																					if (!value && !phone) {
																						return Promise.reject("Please provide either email or phone number");
																					}
																					return Promise.resolve();
																				},
																			}),
																		]}
																	>
																		<Input prefix={<MailOutlined className="text-slate-600" />} placeholder="Email Address" size="large" className="rounded-lg" />
																	</Form.Item>
																	<Form.Item
																		name={[`label_${labelId}`, "phone"]}
																		rules={[
																			({ getFieldValue }) => ({
																				validator(_, value) {
																					const email = getFieldValue([`label_${labelId}`, "email"]);
																					if (!value && !email) {
																						return Promise.reject("Please provide either email or phone number");
																					}
																					return Promise.resolve();
																				},
																			}),
																		]}
																	>
																		<Input prefix={<PhoneOutlined className="text-slate-600" />} placeholder="Phone Number" size="large" className="rounded-lg" />
																	</Form.Item>
																</div>
															)}
														</div>
													)}
												</Card>
											</div>
										);
									}),
								)}

								<Button
									type="primary"
									size="large"
									block
									htmlType="submit"
									loading={submitting}
									className="h-14 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl"
									style={{
										borderRadius: "12px",
										background: "linear-gradient(135deg, #000000 0%, #1e293b 100%)",
										border: "none",
									}}
								>
									{submitting ? "Completing Assignment..." : "Complete Assignment"}
									<CheckCircleOutlined className="ml-2" />
								</Button>
							</Form>
						</SectionCard>
					</div>
				)}

				{step === "complete" && (
					<div className="animate-slide-in-left">
						<SectionCard>
							<div className="text-center py-16">
								<CheckCircleOutlined className="text-9xl text-green-500 mb-6" />
								<Title level={2} className="text-gray-800 mb-4">
									Assignment Complete!
								</Title>
								<Text className="text-gray-600 text-lg block mb-8">All test kits have been successfully assigned. Each person will receive an email with instructions.</Text>
								<div className="max-w-md mx-auto bg-gray-50 p-6 rounded-xl">
									<Text className="text-gray-700 block mb-2">
										<strong>Order #{order?.shopify_order_number || order?.sales_order_id}</strong>
									</Text>
									<Text className="text-gray-600 block">
										{totalLabels} test kit{totalLabels > 1 ? "s" : ""} assigned
									</Text>
								</div>
							</div>
						</SectionCard>
					</div>
				)}
			</div>

			{/* Enhanced CSS */}
			<style>{`
				@keyframes slide-in-right {
					from { opacity: 0; transform: translateX(30px); }
					to { opacity: 1; transform: translateX(0); }
				}
				@keyframes slide-in-left {
					from { opacity: 0; transform: translateX(-30px); }
					to { opacity: 1; transform: translateX(0); }
				}
				.animate-slide-in-right {
					animation: slide-in-right 0.6s cubic-bezier(0.4, 0, 0.2, 1);
				}
				.animate-slide-in-left {
					animation: slide-in-left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
				}
				.ant-card {
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
				}
				.ant-btn:hover {
					transform: translateY(-2px);
					box-shadow: 0 8px 25px rgba(30, 41, 59, 0.4);
				}
				.ant-input:focus, .ant-input-focused {
					border-color: var(--mg-text);
					box-shadow: 0 0 0 2px rgba(30, 41, 59, 0.15);
				}
			`}</style>
		</div>
	);
};

export default OrderAssignmentPage;
